import { useState, useCallback, useRef } from 'react'
import {
  Search, Clipboard, Loader2, Download, RotateCcw,
  AlertCircle, Clock, Zap, Music2, Video, User, HardDrive,
} from 'lucide-react'
import { getInfo, convert } from '../../lib/api.js'
import { useLanguage } from '../../context/LanguageContext.jsx'

function cn(...args) { return args.filter(Boolean).join(' ') }

function isValidUrl(str) {
  try { new URL(str); return true } catch { return false }
}

function fmtDuration(s) {
  if (!s || s <= 0) return null
  const h   = Math.floor(s / 3600)
  const m   = Math.floor((s % 3600) / 60)
  const sec = Math.floor(s % 60)
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`
}

function fmtBytes(bytes) {
  if (!bytes || bytes <= 0) return null
  if (bytes >= 1_073_741_824) return (bytes / 1_073_741_824).toFixed(1) + ' GB'
  if (bytes >= 1_048_576)     return (bytes / 1_048_576).toFixed(1) + ' MB'
  return (bytes / 1024).toFixed(0) + ' KB'
}

// Estimate MP3 file size from duration + average VBR0 bitrate (~245 kbps)
function mp3SizeEstimate(durationSec) {
  if (!durationSec || durationSec <= 0) return null
  return Math.round(durationSec * 245 * 1000 / 8)
}

// Single high-quality MP3 option — no kbps display
const AUDIO_ROW = {
  format:     'mp3',
  quality:    '320',           // maps to yt-dlp VBR 0 (best quality, ~245 kbps variable)
  label:      'MP3',
  badge:      'High Quality',
  badgeColor: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
}

const VIDEO_BADGE = {
  2160: { text: '4K',  color: 'bg-orange-500/15 text-orange-600 dark:text-orange-400' },
  1440: { text: '2K',  color: 'bg-pink-500/15 text-pink-600 dark:text-pink-400' },
  1080: { text: 'FHD', color: 'bg-violet-500/15 text-violet-600 dark:text-violet-400' },
  720:  { text: 'HD',  color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400' },
  480:  { text: null,  color: '' },
  360:  { text: null,  color: '' },
  240:  { text: null,  color: '' },
  144:  { text: null,  color: '' },
}

// ─────────────────────────────────────────────────────────────────
// ConverterBox — quality table
//
// Flow:  idle → fetching (auto on valid URL)
//        → ready (Quality | File size | Download table)
//        → converting (spinner in row) → done | error
// ─────────────────────────────────────────────────────────────────

export default function ConverterBox({
  provider,
  placeholder,
  cta,
}) {
  const { t } = useLanguage()
  const resolvedPlaceholder = placeholder || t('genericPlaceholder')
  const [url,           setUrl]           = useState('')
  const [status,        setStatus]        = useState('idle')
  const [error,         setError]         = useState(null)
  const [mediaInfo,     setMediaInfo]     = useState(null)
  const [convertingFmt, setConvertingFmt] = useState(null)
  const [doneFmt,       setDoneFmt]       = useState(null)

  const inputRef = useRef(null)

  const isValid    = url && isValidUrl(url.trim())
  const isInvalid  = url && !isValidUrl(url.trim())
  const isFetching = status === 'fetching'
  const busy       = isFetching || !!convertingFmt

  // ── fetch metadata ──────────────────────────────────────────
  const fetchInfo = useCallback(async (u) => {
    setStatus('fetching')
    setError(null)
    setMediaInfo(null)
    setConvertingFmt(null)
    setDoneFmt(null)
    try {
      const info = await getInfo(u)
      setMediaInfo(info)
      setStatus('ready')
    } catch (err) {
      setError(err.message)
      setStatus('error')
    }
  }, [])

  // ── URL input change ─────────────────────────────────────────
  const handleUrlChange = (val) => {
    setUrl(val)
    if (status !== 'idle') {
      setStatus('idle')
      setError(null)
      setMediaInfo(null)
      setConvertingFmt(null)
      setDoneFmt(null)
    }
  }

  // The form submit is intentionally the single metadata lookup path. This
  // keeps the visible CTA, Enter, and a pasted valid URL behaviour aligned.
  const submitUrl = useCallback((event, submittedUrl = url) => {
    event?.preventDefault()
    const trimmed = submittedUrl.trim()
    if (!trimmed || !isValidUrl(trimmed) || busy) return
    fetchInfo(trimmed)
  }, [url, busy, fetchInfo])

  // ── paste ─────────────────────────────────────────────────────
  const paste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText()
      if (!text) return
      const trimmed = text.trim()
      setUrl(trimmed)
      setStatus('idle')
      setError(null)
      setMediaInfo(null)
      setConvertingFmt(null)
      setDoneFmt(null)
      if (isValidUrl(trimmed)) {
        submitUrl(null, trimmed)
      }
    } catch { /* clipboard access denied */ }
  }, [submitUrl])

  // ── start download for a specific row ────────────────────────
  const handleDownload = useCallback(async (format, quality, label) => {
    if (convertingFmt) return
    setConvertingFmt({ format, quality, label })
    setDoneFmt(null)
    setError(null)
    try {
      const data = await convert({ url: url.trim(), provider, format, quality })
      setDoneFmt({ format, quality, label, downloadUrl: data.downloadUrl })
      setConvertingFmt(null)
    } catch (err) {
      setError(err.message)
      setConvertingFmt(null)
    }
  }, [url, provider, convertingFmt])

  // ── reset ────────────────────────────────────────────────────
  const reset = useCallback(() => {
    setUrl('')
    setStatus('idle')
    setError(null)
    setMediaInfo(null)
    setConvertingFmt(null)
    setDoneFmt(null)
    inputRef.current?.focus()
  }, [])

  const duration = mediaInfo?.duration ?? null

  // Single audio row enriched with estimated file size
  const audioRow = {
    ...AUDIO_ROW,
    filesize: mp3SizeEstimate(duration),
  }

  // Video rows from backend — dynamic, no hardcoding
  const videoRows = []
  if (mediaInfo?.videoFormats && mediaInfo.videoFormats.length > 0) {
    videoRows.push(
      ...mediaInfo.videoFormats.map(({ height, label, filesize }) => {
        const b = VIDEO_BADGE[height] || { text: null, color: '' }
        return {
          format:    'mp4',
          quality:   String(height),
          label,
          badge:     b.text,
          badgeColor: b.color,
          filesize,
        }
      })
    )
  }
  // If no videoFormats but formats includes mp4, show MP4 with no quality badge
  if (!(mediaInfo?.videoFormats ?? []).length && mediaInfo?.formats?.includes({ id: 'mp4' })) {
    videoRows.push({
      format:    'mp4',
      quality:   'unknown',
      label:     'MP4 Video',
      badge:     null,
      badgeColor: '',
      filesize:  null,
    })
  }

  const rowState = (format, quality) => {
    if (convertingFmt?.format === format && convertingFmt?.quality === quality) return 'converting'
    if (doneFmt?.format === format && doneFmt?.quality === quality) return 'done'
    return 'idle'
  }

  return (
    <div>
      <div className="glass-card rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-10">

        {/* ── URL input ───────────────────────────────────────── */}
        <form className="w-full" onSubmit={submitUrl} aria-busy={busy}>
          <div className={cn(
            'relative flex items-center border-2 rounded-2xl transition-all duration-200',
            'bg-gradient-to-r from-card/60 to-card/40 backdrop-blur-sm shadow-sm',
            isInvalid
              ? 'border-destructive/50'
              : 'border-primary/30 hover:border-primary/60 focus-within:border-primary focus-within:shadow-lg focus-within:shadow-primary/15',
          )}>
            <div className="absolute left-4 sm:left-5 text-muted-foreground pointer-events-none flex-shrink-0">
              {isFetching
                ? <Loader2 className="w-5 h-5 animate-spin text-primary" />
                : <Search className="w-5 h-5" />}
            </div>
            <input
              ref={inputRef}
              type="url"
              placeholder={resolvedPlaceholder}
              className="flex-1 h-14 sm:h-16 pl-12 sm:pl-14 pr-4 text-sm sm:text-base bg-transparent border-none outline-none text-foreground font-medium min-w-0 placeholder-muted-foreground/50"
              value={url}
              onChange={e => handleUrlChange(e.target.value)}
              disabled={busy}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
            <div className="flex items-center gap-2 pr-3 sm:pr-4 flex-shrink-0">
              <button
                type="button"
                aria-label="Paste URL"
                onClick={paste}
                disabled={busy}
                className="h-9 sm:h-10 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-semibold text-primary bg-primary/10 hover:bg-primary/20 transition-all disabled:opacity-50 active:scale-95 flex items-center gap-1.5"
              >
                <Clipboard className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{t('paste')}</span>
              </button>
              {status !== 'ready' && (
                <button
                  type="submit"
                  disabled={!isValid || busy}
                  className="h-9 sm:h-10 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 flex items-center gap-1.5"
                >
                  {isFetching
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                    : <Zap className="w-3.5 h-3.5" aria-hidden="true" />}
                   <span>{isFetching ? 'Loading…' : (cta || t('convert'))}</span>
                </button>
              )}
              {status === 'ready' && (
                <button
                  type="button"
                  onClick={reset}
                  className="h-9 sm:h-10 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-semibold text-primary bg-primary/10 hover:bg-primary/20 transition-all active:scale-95 flex items-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t('new')}</span>
                </button>
              )}
            </div>
          </div>
          <p className="sr-only" role="status" aria-live="polite">
            {isFetching ? 'Fetching media information.' : convertingFmt ? `Converting ${convertingFmt.label}.` : ''}
          </p>

          {isInvalid && (
            <p className="flex items-center gap-1.5 text-destructive text-sm mt-2 ml-1">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              {t('invalidUrl')}
            </p>
          )}
        </form>

        {/* ── Metadata + quality table ─────────────────────────── */}
        {mediaInfo && (
          <div className="mt-6 animate-fade-in space-y-4">

            {/* Thumbnail + title + duration + uploader + filesize */}
            <div className="flex gap-4 p-4 bg-primary/5 border border-primary/15 rounded-xl items-start">
              {mediaInfo.thumbnail && (
                <img
                  src={mediaInfo.thumbnail}
                  alt=""
                  className="w-28 h-[72px] sm:w-36 sm:h-[90px] object-cover rounded-lg flex-shrink-0 bg-muted"
                  onError={e => { e.currentTarget.style.display = 'none' }}
                />
              )}
              <div className="flex-1 min-w-0 pt-0.5 space-y-1">
                <p className="font-semibold text-sm sm:text-base leading-snug line-clamp-2 text-foreground">
                  {mediaInfo.title}
                </p>
                {mediaInfo.uploader && (
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <User className="w-3 h-3 flex-shrink-0" />
                    {mediaInfo.uploader}
                  </p>
                )}
                {fmtDuration(mediaInfo.duration) && (
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    {t('duration')}: {fmtDuration(mediaInfo.duration)}
                  </p>
                )}
                {mediaInfo.filesize && fmtBytes(mediaInfo.filesize) && (
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <HardDrive className="w-3 h-3 flex-shrink-0" />
                    ~{fmtBytes(mediaInfo.filesize)}
                  </p>
                )}
              </div>
            </div>

            {/* Quality table */}
            {status === 'ready' && (
              <div className="rounded-xl border border-border/60 overflow-hidden">

                {/* Table header — 3 columns */}
                <div className="grid grid-cols-[1fr_100px_auto] sm:grid-cols-[1fr_120px_auto] bg-gradient-to-r from-primary to-accent px-4 py-2.5 gap-2">
                  <span className="text-xs font-bold text-white uppercase tracking-wider">{t('quality')}</span>
                  <span className="text-xs font-bold text-white uppercase tracking-wider text-center">{t('fileSize')}</span>
                  <span className="text-xs font-bold text-white uppercase tracking-wider text-right">{t('download')}</span>
                </div>

                <div className="divide-y divide-border/40">

                  {/* ── Audio section — single high-quality MP3 row ── */}
                  <div className="px-4 py-1.5 bg-muted/30 flex items-center gap-1.5">
                    <Music2 className="w-3 h-3 text-primary" />
                    <span className="text-[10px] font-bold text-primary uppercase tracking-widest">{t('audio')}</span>
                  </div>
                  {(() => {
                    const rs = rowState(audioRow.format, audioRow.quality)
                    return (
                      <QualityRow
                        key="mp3-hq"
                        label={audioRow.label}
                        badge={audioRow.badge}
                        badgeColor={audioRow.badgeColor}
                        filesize={audioRow.filesize}
                        rowState={rs}
                        doneFmt={rs === 'done' ? doneFmt : null}
                        disabled={!!convertingFmt && rs !== 'converting'}
                        onDownload={() => handleDownload(audioRow.format, audioRow.quality, audioRow.label)}
                      />
                    )
                  })()}

                  {/* ── Video section — dynamic from backend ── */}
                  {videoRows.length > 0 && (
                    <>
                      <div className="px-4 py-1.5 bg-muted/30 flex items-center gap-1.5">
                        <Video className="w-3 h-3 text-accent" />
                        <span className="text-[10px] font-bold text-accent uppercase tracking-widest">{t('video')}</span>
                      </div>
                      {videoRows.map(({ format, quality, label, badge, badgeColor, filesize }) => {
                        const rs = rowState(format, quality)
                        return (
                          <QualityRow
                            key={`${format}-${quality}`}
                            label={label}
                            badge={badge}
                            badgeColor={badgeColor}
                            filesize={filesize}
                            rowState={rs}
                            doneFmt={rs === 'done' ? doneFmt : null}
                            disabled={!!convertingFmt && rs !== 'converting'}
                            onDownload={() => handleDownload(format, quality, label)}
                          />
                        )
                      })}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Converting progress bar */}
            {convertingFmt && (
              <div className="space-y-1.5">
                <div className="flex justify-between items-center text-xs font-medium text-primary">
                  <span>{t('converting').replace('…', '')} {convertingFmt.label}…</span>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                </div>
                <div className="h-1.5 bg-primary/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-primary to-accent rounded-full animate-progress-bar" />
                </div>
              </div>
            )}

            {/* Convert another */}
            {doneFmt && !convertingFmt && (
              <button
                onClick={reset}
                className="w-full h-10 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                {t('convertAnotherUrl')}
              </button>
            )}
          </div>
        )}

        {/* ── Error (fetch failed) ───────────────────────────────── */}
        {status === 'error' && error && (
          <div className="mt-5 p-4 sm:p-5 bg-destructive/8 border border-destructive/20 rounded-2xl animate-fade-in">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-destructive mb-1 text-sm">
                  {t('couldNotFetch')}
                </p>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
            </div>
            <button
              onClick={reset}
              className="mt-4 h-9 px-4 text-sm font-medium rounded-lg text-muted-foreground hover:bg-primary/5 transition-colors"
            >
              {t('tryAnotherUrl')}
            </button>
          </div>
        )}

        {/* ── Per-row conversion error ───────────────────────────── */}
        {error && status === 'ready' && (
          <div className="mt-3 flex items-start gap-2 p-3 bg-destructive/8 border border-destructive/20 rounded-xl animate-fade-in">
            <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground flex-1">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              aria-label="Dismiss"
            >✕</button>
          </div>
        )}

      </div>
    </div>
  )
}

// ─── Quality Row ──────────────────────────────────────────────────
function QualityRow({ label, badge, badgeColor, filesize, rowState, doneFmt, disabled, onDownload }) {
  const { t } = useLanguage()
  const sizeStr = filesize ? fmtBytes(filesize) : '—'

  return (
    <div className={cn(
      'grid grid-cols-[1fr_100px_auto] sm:grid-cols-[1fr_120px_auto] items-center px-4 py-3 gap-2 transition-colors',
      rowState === 'converting' ? 'bg-primary/5' : '',
      rowState === 'done' ? 'bg-emerald-500/5' : '',
      !disabled && rowState === 'idle' ? 'hover:bg-muted/30' : '',
    )}>
      {/* Label + badge */}
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-semibold text-sm text-foreground">{label}</span>
        {badge && (
          <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-bold flex-shrink-0', badgeColor)}>
            {badge}
          </span>
        )}
      </div>

      {/* File size */}
      <div className="text-center">
        <span className="text-xs text-muted-foreground font-medium tabular-nums">{sizeStr}</span>
      </div>

      {/* Action */}
      <div className="flex-shrink-0 flex justify-end">
        {rowState === 'converting' ? (
          <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-primary/10 text-primary text-xs font-semibold">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span className="hidden sm:inline">{t('converting')}</span>
          </span>
        ) : rowState === 'done' && doneFmt ? (
          <a
            href={doneFmt.downloadUrl}
            download
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold transition-colors shadow-sm shadow-emerald-500/30"
          >
            <Download className="w-3 h-3" />
            {t('save')}
          </a>
        ) : (
          <button
            onClick={onDownload}
            disabled={disabled}
            className={cn(
              'inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-bold transition-all',
              disabled
                ? 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
                : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-sm shadow-green-500/20 hover:shadow-green-500/40 active:scale-95',
            )}
          >
            <Zap className="w-3 h-3" />
            <span>{t('download')}</span>
          </button>
        )}
      </div>
    </div>
  )
}
